import styled from 'styled-components'

import { Numeric } from '@blade/ui-kit'
import { type CurrencyModel } from '@root/entities/currency'
import { useTranslation } from '@root/shared/localization'

import type BigNumber from 'bignumber.js'

type TradingPositionPriceProps = {
  type: 'entry' | 'target'
  price: BigNumber | undefined
  marketCap: BigNumber | undefined
  currency: CurrencyModel
}

export const TradingPositionPrice: React.FC<TradingPositionPriceProps> = ({
  type,
  price,
  marketCap,
  currency,
}) => {
  const { t } = useTranslation()

  const renderPrice = () => {
    if (!price) {
      return <>{t('trading.positions.not-set').toUpperCase()}</>
    }

    return (
      <>
        <Numeric
          value={price}
          preset="currency-amount-short"
          currency={currency.ticker}
        />
      </>
    )
  }

  const renderMarketCapacity = () => {
    if (!marketCap) {
      return <>&nbsp;</>
    }

    return (
      <>
        MC $<Numeric value={marketCap} preset="currency-amount-short" />
      </>
    )
  }

  return (
    <Root>
      <Label>
        {type === 'entry'
          ? t('position.entry-price')
          : t('position.target-price')}
      </Label>
      <Price $price={price}>{renderPrice()}</Price>
      <MarketCapacity>{renderMarketCapacity()}</MarketCapacity>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
`

const Label = styled.div`
  color: var(--text-8);
  font-size: 6px;
  font-weight: 500;
  text-transform: uppercase;

  margin-bottom: 2px;
`

const Price = styled.div<{ $price: object | undefined }>`
  color: ${({ $price }) =>
    $price ? 'var(--background-12)' : 'var(--background-8)'};
  text-align: left;
  font-size: 12px;
  font-weight: 700;
  line-height: 12px;

  & sub {
    font-size: 8px;
    font-weight: 600;
    line-height: 12px;
    padding-inline: 1px;
  }

  margin-bottom: 1px;
`

const MarketCapacity = styled.div`
  color: var(--text-8);
  font-size: 8px;
  font-weight: 500;
`
